import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RSVP_FORM } from "@/constants/testIds";
import { Check, Send } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { DahliaFlourish, RoseFlourish } from "@/components/wedding/Flourish";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  attending: "yes",
  guest_count: 1,
  plus_one: false,
  plus_one_name: "",
  meal_preference: "chicken",
  song_request: "",
  message: "",
  city: "",
  country: "",
};

export const RsvpForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error(t("rsvp.errorMissing"));
      return;
    }
    if (form.attending === "yes" && form.plus_one && !form.plus_one_name.trim()) {
      toast.error(t("rsvp.errorPlusOneName"));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email || !payload.email.trim()) payload.email = null;
      if (form.attending === "no") {
        payload.guest_count = 1;
        payload.plus_one = false;
        payload.plus_one_name = null;
        payload.meal_preference = null;
      } else {
        payload.guest_count = form.plus_one ? 2 : 1;
        payload.plus_one_name = form.plus_one ? form.plus_one_name.trim() : null;
      }
      await axios.post(`${API}/rsvp`, payload);
      setDone(true);
      toast.success(t("rsvp.toastSuccess"));
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : t("rsvp.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const heading = t("rsvp.heading");
  const meals = t("rsvp.meals");

  return (
    <section id="rsvp" data-testid={RSVP_FORM.section} className="relative py-24 sm:py-32 overflow-hidden">
      <DahliaFlourish
        className="absolute -top-6 -right-6 w-[100px] sm:w-[140px] md:w-[180px]"
        opacity={0.75}
      />
      <RoseFlourish
        className="absolute -bottom-8 -left-6 w-[100px] sm:w-[140px] md:w-[180px] -rotate-3"
        opacity={0.75}
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              {heading[0]}<em className="italic">{heading[1]}</em>
            </h2>
            <p className="mt-8 font-serif italic text-xl text-black/70 max-w-md">{t("rsvp.sub")}</p>
            <div className="hairline mt-10" />
            <div className="mt-6 space-y-6 max-w-md">
              <div>
                <div className="font-overline text-[10px] text-black/60">{t("rsvp.replyBy")}</div>
                <div className="font-display text-2xl mt-1">{t("rsvp.replyByValue")}</div>
              </div>
              <div>
                <div className="font-overline text-[10px] text-black/60">{t("rsvp.questions")}</div>
                <a
                  href={`mailto:${t("rsvp.contactEmail")}`}
                  className="mt-1 block font-serif text-lg sm:text-xl italic text-black/85 hover:text-[color:var(--sunset-600)] transition-colors break-all"
                >
                  {t("rsvp.contactEmail")}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid={RSVP_FORM.success}
              className="border border-black p-10 sm:p-14 bg-white relative"
            >
              <div className="absolute -top-6 -left-6 w-12 h-12 bg-[color:var(--sunset-500)] text-white flex items-center justify-center">
                <Check size={20} />
              </div>
              <div className="font-overline text-[11px] text-[color:var(--sunset-600)]">{t("rsvp.successEyebrow")}</div>
              <h3 className="mt-4 font-display text-4xl sm:text-5xl tracking-tighter">{t("rsvp.successTitle")}</h3>
              <p className="mt-6 font-serif italic text-lg text-black/70 max-w-md">{t("rsvp.successBody")}</p>
              <button
                onClick={() => setDone(false)}
                className="mt-10 font-overline text-[11px] border border-black bg-black text-white px-6 py-3 hover:bg-transparent hover:text-black transition-colors"
              >
                {t("rsvp.submitAnother")}
              </button>
            </div>
          ) : (
            <form
              data-testid={RSVP_FORM.form}
              onSubmit={submit}
              className="border border-black/15 p-8 sm:p-12 bg-white"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="font-overline text-[10px] text-black/60">{t("rsvp.fullName")}</label>
                  <input
                    data-testid={RSVP_FORM.fullName}
                    type="text"
                    className="editorial-input"
                    placeholder={t("rsvp.fullNamePh")}
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="font-overline text-[10px] text-black/60">
                    {t("rsvp.email")} <span className="opacity-50 normal-case">{t("rsvp.optional")}</span>
                  </label>
                  <input
                    data-testid={RSVP_FORM.email}
                    type="email"
                    className="editorial-input"
                    placeholder={t("rsvp.emailPh")}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-10">
                <div className="font-overline text-[10px] text-black/60 mb-4">{t("rsvp.attending")}</div>
                <RadioGroup
                  value={form.attending}
                  onValueChange={(v) => update("attending", v)}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  <Label
                    htmlFor="rsvp-yes"
                    className={`cursor-pointer border p-5 flex items-center gap-3 rounded-none transition-colors ${
                      form.attending === "yes" ? "border-black bg-black text-white" : "border-black/20 hover:border-black/60"
                    }`}
                  >
                    <RadioGroupItem
                      data-testid={RSVP_FORM.attendingYes}
                      id="rsvp-yes"
                      value="yes"
                      className="rounded-none"
                    />
                    <span className="font-serif text-xl italic">{t("rsvp.yes")}</span>
                  </Label>
                  <Label
                    htmlFor="rsvp-no"
                    className={`cursor-pointer border p-5 flex items-center gap-3 rounded-none transition-colors ${
                      form.attending === "no" ? "border-black bg-black text-white" : "border-black/20 hover:border-black/60"
                    }`}
                  >
                    <RadioGroupItem
                      data-testid={RSVP_FORM.attendingNo}
                      id="rsvp-no"
                      value="no"
                      className="rounded-none"
                    />
                    <span className="font-serif text-xl italic">{t("rsvp.no")}</span>
                  </Label>
                </RadioGroup>
              </div>

              {form.attending === "yes" && (
                <>
                  <div className="mt-10">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        data-testid={RSVP_FORM.plusOneToggle}
                        type="checkbox"
                        className="h-5 w-5 rounded-none border border-black/40 accent-black cursor-pointer"
                        checked={form.plus_one}
                        onChange={(e) => update("plus_one", e.target.checked)}
                      />
                      <span className="font-serif text-lg italic">{t("rsvp.plusOne")}</span>
                    </label>
                    {form.plus_one && (
                      <div className="mt-6">
                        <label className="font-overline text-[10px] text-black/60">{t("rsvp.plusOneName")}</label>
                        <input
                          data-testid={RSVP_FORM.plusOneName}
                          type="text"
                          className="editorial-input"
                          placeholder={t("rsvp.plusOneNamePh")}
                          value={form.plus_one_name}
                          onChange={(e) => update("plus_one_name", e.target.value)}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-10">
                    <label className="font-overline text-[10px] text-black/60">{t("rsvp.meal")}</label>
                    <Select
                      value={form.meal_preference}
                      onValueChange={(v) => update("meal_preference", v)}
                    >
                      <SelectTrigger
                        data-testid={RSVP_FORM.mealTrigger}
                        className="editorial-input h-auto"
                      >
                        <SelectValue placeholder={t("rsvp.mealChoose")} />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {["chicken", "beef", "fish", "pork"].map((m) => (
                          <SelectItem
                            data-testid={RSVP_FORM.mealOption(m)}
                            key={m}
                            value={m}
                            className="rounded-none"
                          >
                            {meals[m]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="mt-10">
                <div className="font-overline text-[10px] text-black/60 mb-4">{t("rsvp.origin")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="font-overline text-[10px] text-black/60">
                      {t("rsvp.city")} <span className="opacity-50 normal-case">{t("rsvp.optional")}</span>
                    </label>
                    <input
                      data-testid="rsvp-city"
                      type="text"
                      className="editorial-input"
                      placeholder={t("rsvp.cityPh")}
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="font-overline text-[10px] text-black/60">
                      {t("rsvp.country")} <span className="opacity-50 normal-case">{t("rsvp.optional")}</span>
                    </label>
                    <input
                      data-testid="rsvp-country"
                      type="text"
                      className="editorial-input"
                      placeholder={t("rsvp.countryPh")}
                      value={form.country}
                      onChange={(e) => update("country", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <label className="font-overline text-[10px] text-black/60">
                  {t("rsvp.song")} <span className="opacity-50 normal-case">{t("rsvp.optional")}</span>
                </label>
                <input
                  data-testid={RSVP_FORM.songRequest}
                  type="text"
                  className="editorial-input"
                  placeholder={t("rsvp.songPh")}
                  value={form.song_request}
                  onChange={(e) => update("song_request", e.target.value)}
                />
              </div>

              <div className="mt-10">
                <label className="font-overline text-[10px] text-black/60">
                  {t("rsvp.note")} <span className="opacity-50 normal-case">{t("rsvp.optional")}</span>
                </label>
                <textarea
                  data-testid={RSVP_FORM.message}
                  className="editorial-input min-h-[100px] resize-y"
                  placeholder={t("rsvp.notePh")}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                />
              </div>

              <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
                <div className="font-serif italic text-sm text-black/50 max-w-xs">
                  {t("rsvp.footer")}
                </div>
                <button
                  data-testid={RSVP_FORM.submit}
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 border border-black bg-black text-white px-8 py-4 font-overline text-[11px] hover:bg-[color:var(--sunset-500)] hover:border-[color:var(--sunset-500)] transition-colors disabled:opacity-60"
                >
                  {loading ? t("rsvp.submitting") : t("rsvp.submit")}
                  <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
